/**
 * Authenticated API Service
 * 
 * Provides methods to make authenticated API calls with MSAL bearer tokens.
 * Automatically acquires and attaches access tokens to all requests.
 */

import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, apiRequest } from './authConfig';

// Get the MSAL instance - this should be the same instance used by the app
let msalInstance = null;

/**
 * Initialize the API service with the MSAL instance
 * Call this once when the app initializes
 * @param {PublicClientApplication} instance - The MSAL instance
 */
export function initializeApiService(instance) {
    msalInstance = instance;
}

/**
 * Get the current MSAL instance
 * @returns {PublicClientApplication|null}
 */
export function getMsalInstance() {
    return msalInstance;
}

/**
 * Acquire an access token silently for API calls
 * Falls back to interactive login if silent acquisition fails
 * @returns {Promise<string|null>} - The access token or null if acquisition fails
 */
export async function getAccessToken() {
    if (!msalInstance) {
        console.error('MSAL instance not initialized. Call initializeApiService first.');
        return null;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
        console.error('No accounts found. User needs to sign in.');
        return null;
    }

    const account = accounts[0];

    try {
        // Try to acquire token silently
        const response = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: account
        });
        return response.accessToken;
    } catch (error) {
        console.warn('Silent token acquisition failed, attempting interactive:', error);
        
        try {
            // Fall back to interactive token acquisition
            const response = await msalInstance.acquireTokenPopup(apiRequest);
            return response.accessToken;
        } catch (interactiveError) {
            console.error('Interactive token acquisition failed:', interactiveError);
            return null;
        }
    }
}

/**
 * Make an authenticated fetch request with bearer token
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - The fetch response
 */
export async function authFetch(url, options = {}) {
    const token = await getAccessToken();
    
    if (!token) {
        throw new Error('Unable to acquire access token for API call');
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
    };

    return fetch(url, {
        ...options,
        headers
    });
}

/**
 * GET request with authentication
 * @param {string} url - The URL to fetch
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function authGet(url, options = {}) {
    return authFetch(url, {
        method: 'GET',
        ...options
    });
}

/**
 * POST request with authentication
 * @param {string} url - The URL to post to
 * @param {object} data - The data to send in the request body
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function authPost(url, data, options = {}) {
    return authFetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        ...options
    });
}

/**
 * PUT request with authentication
 * @param {string} url - The URL to put to
 * @param {object} data - The data to send in the request body
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function authPut(url, data, options = {}) {
    return authFetch(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        ...options
    });
}

/**
 * DELETE request with authentication
 * @param {string} url - The URL to delete
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function authDelete(url, options = {}) {
    return authFetch(url, {
        method: 'DELETE',
        ...options
    });
}
